import { useMemo } from 'react';
import { SearchFactory } from 'src/domain';
import { useSearchPreferences } from 'src/react/shared/api';
import { useSiteConfig } from 'src/react/config';
import useSearchProfileAdapter from '../searchProfile/useSearchProfileAdapter';

/**
 * The factory every legacy search is made from, built from what the visitor's
 * session already carries: their stored preferences, their profile's sort
 * defaults, and the site's parameter exclusions.
 *
 * Reads exactly what the scope-change resolver it replaces read, so a hook that
 * switches to it needs no new providers — which is what keeps the legacy
 * callers of `useSearch` untouched.
 *
 * Built once rather than per render: the exclusions come from site config and
 * do not change while the app runs, and a fresh factory each time would change
 * this hook's return identity on every render.
 */
const useSearchFactoryAdapter = (): SearchFactory => {
  const searchProfile = useSearchProfileAdapter();
  const preferences = useSearchPreferences(searchProfile);
  const { searchParameters: siteExclusions } = useSiteConfig();

  return useMemo(
    () =>
      new SearchFactory({
        preferences,
        sortProfile: {
          botOrFirstTimeVisitor: searchProfile.botOrFirstTimeVisitor,
        },
        siteExclusions,
      }),
    [preferences, searchProfile.botOrFirstTimeVisitor, siteExclusions]
  );
};

export default useSearchFactoryAdapter;
